import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  Box,
  ButtonBase,
  Divider,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  SxProps,
} from '@mui/material';
import { Insight } from '../../pages/insights/types';
import {
  AccessTime,
  ErrorOutline,
  GridViewOutlined,
  LightbulbOutlined,
  Star,
  Search,
  SearchOff,
} from '@mui/icons-material';
import { escapeRegex, stableSort, sortAlphaNum } from '../../utils/utils';
import useRecentInsights from '../../utils/hooks/useRecentInsights';
import { useGetInsightsQuery } from '../../store/slices/apiSlice';
import { selectAgency } from '../../store/slices/userSlice';

type SearchResultsListProps = {
  inputRef?: React.RefObject<HTMLInputElement>;
  closeModal?: () => void;
  search?: string;
  setSearch?: (search: string) => void;
  onInsightClick: (insight: Insight) => void;
};

type SearchResultsMessageProps = {
  Icon: React.FC<{ sx: SxProps }>;
  title: string;
};

function SearchResultsMessage({ Icon, title }: SearchResultsMessageProps) {
  return (
    <Box
      sx={{
        px: 1,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        minHeight: 40,
      }}
    >
      <Icon sx={{ mr: 1 }} />
      {title}
    </Box>
  );
}

export default function SearchResultsList({
  inputRef,
  closeModal,
  search,
  setSearch,
  onInsightClick,
}: SearchResultsListProps) {
  const history = useHistory();
  const { agencyId } = useSelector(selectAgency);
  const { data = [], isLoading, isError } = useGetInsightsQuery({ agencyId });
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const listItemRefs = useRef<(HTMLButtonElement | HTMLDivElement | null)[]>(
    []
  );
  const { recentInsights } = useRecentInsights();

  const sortedData = useMemo(() => {
    const filteredInsights = stableSort(data, (insightA, insightB) =>
      sortAlphaNum(insightA.name, insightB.name)
    );

    if (search) {
      const searchExpression = new RegExp(escapeRegex(search), 'gi');
      return filteredInsights.filter((insight) =>
        insight.name.match(searchExpression)
      );
    } else {
      return filteredInsights.filter(
        (insight) =>
          insight.isFavorited === true || recentInsights.includes(insight.id)
      );
    }
  }, [data, recentInsights, search]);

  useEffect(() => {
    if (focusedIndex !== null) {
      setFocusedIndex(0);
    }
  }, [sortedData]);

  // toggle fake focus up and down in list, allowing it to go over the last index to focus
  // the overview button
  const refocusOnArrow = useCallback(
    (e: React.KeyboardEvent) => {
      let newIndex = focusedIndex;
      if (e.key === 'ArrowUp') {
        if (focusedIndex !== null && focusedIndex > 0) {
          newIndex = focusedIndex - 1;
        } else {
          newIndex = sortedData.length;
        }
      } else if (e.key === 'ArrowDown') {
        if (focusedIndex !== null && focusedIndex < sortedData.length) {
          newIndex = focusedIndex + 1;
        } else {
          newIndex = 0;
        }
      }
      setFocusedIndex(newIndex);
    },
    [sortedData, focusedIndex]
  );

  const resetAndClose = useCallback(() => {
    setSearch?.('');
    closeModal?.();
  }, [setSearch, closeModal]);

  // if no focused index, open overview
  // if focused, open insight or overview depending on what was focused
  // reset focused index for the next time the search is opened
  const openFocusedInsight = useCallback(() => {
    setFocusedIndex(null);
    if (focusedIndex !== null) {
      if (focusedIndex === sortedData.length) {
        resetAndClose();
        history.push('/views/all');
      } else {
        onInsightClick(sortedData[focusedIndex]);
      }
    } else {
      resetAndClose();
      history.push('/views/all');
    }
  }, [focusedIndex, history, resetAndClose, sortedData, onInsightClick]);

  const handleKeyup = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          refocusOnArrow(e);
          break;
        case 'Enter':
          openFocusedInsight();
          break;
      }
    },
    [refocusOnArrow, openFocusedInsight]
  );

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch?.(e.currentTarget.value);
    },
    [setSearch]
  );

  let listBody = (
    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
      {sortedData.map((insight, i) => {
        const isFocused = i === focusedIndex;
        let icon;
        if (insight.isFavorited) {
          icon = <Star />;
        } else if (recentInsights.includes(insight.id)) {
          icon = <AccessTime />;
        }
        return (
          <ListItem
            disablePadding
            disableGutters
            key={insight.id}
            aria-label={`${insight.name}, open this report`}
            aria-selected={isFocused}
          >
            <ListItemButton
              disableGutters
              key={insight.id}
              onClick={() => onInsightClick(insight)}
              sx={{
                px: 1,
                minHeight: 40,
                '[aria-selected=true] &': {
                  backgroundColor: 'rgba(12, 12, 12, 0.04)',
                },
              }}
              ref={(ref) => (listItemRefs.current[i] = ref)}
            >
              {icon && (
                <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
              )}
              <ListItemText
                sx={{
                  pl: icon ? 0 : 4,
                  pr: 1,
                  fontSize: '0.875rem',
                }}
              >
                {insight.name}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  if (sortedData.length === 0) {
    if (search && search.length > 0) {
      listBody = (
        <SearchResultsMessage Icon={SearchOff} title='No reports found' />
      );
    } else {
      listBody = (
        <SearchResultsMessage
          Icon={LightbulbOutlined}
          title='Your favorites and recents will show up here'
        />
      );
    }
  }

  if (isLoading) {
    listBody = (
      <List>
        {Array.apply(null, Array(3)).map((_, i) => (
          <ListItem disableGutters key={i} sx={{ minHeight: 40, px: 1 }}>
            <ListItemIcon>
              <Skeleton variant='circular' width={24} height={24} />
            </ListItemIcon>
            <ListItemText sx={{ pr: 1, fontSize: '0.875rem' }}>
              <Skeleton />
            </ListItemText>
          </ListItem>
        ))}
      </List>
    );
  }

  if (isError) {
    listBody = (
      <SearchResultsMessage
        Icon={ErrorOutline}
        title='Reports could not be fetched, try again later'
      />
    );
  }

  return (
    <Box onKeyUp={handleKeyup}>
      <InputBase
        fullWidth
        inputRef={inputRef}
        placeholder='Search reports...'
        sx={{
          px: 1,
          py: 0.5,
          color: 'secondary.main',
          fontSize: '0.8125rem',
          lineHeight: '1rem',
        }}
        startAdornment={<Search sx={{ mr: 1 }} />}
        value={search}
        onChange={onSearchChange}
      />
      <Divider />
      {listBody}
      <Divider />
      <ButtonBase
        aria-selected={focusedIndex === sortedData.length}
        component={Link}
        sx={{
          py: 1,
          px: 1,
          width: 1,
          justifyContent: 'flex-start',
          fontFamily: 'Roboto, sans-serif',
          '&:hover, &:focus, &[aria-selected=true]': {
            backgroundColor: 'rgba(12, 12, 12, 0.04)',
          },
        }}
        to='/views/all'
        onClick={resetAndClose}
      >
        <GridViewOutlined sx={{ mr: 1 }} />
        View all reports
      </ButtonBase>
    </Box>
  );
}
